import React, { useEffect, useState } from 'react';
import './../AssestUpload/SingleUpload/SingleUploadContent.scss';
import './AssetDetail.scss'; // Import your CSS file for styling
import { CoreAccordion, CoreAccordionItem } from '@komodo-design-system/komodo-components-react';
import { domParser, generateTableData } from 'utils/utils';

interface RightSectionProps {
  assetDetails?: any;
}

const RightSection: React.FC<RightSectionProps> = ({assetDetails}) => {
  const [tableData, setTableData] = useState<any[]>([]); // Replace 'any' with the appropriate type for tableData
  const [accordionState, setAccordionState] = useState<{ [key: string]: boolean }>({}); // Track open state for each accordion

  useEffect(() => {
    const data = generateTableData(assetDetails?.metadata_collection?.metadata_element_list);
    // remove the field_id which is TFS.FIELD.IS_CUSTOM from the tableData
    const filteredData = data?.map((item: any) => {
      return {
        ...item,
        elements: item.elements.filter((field: any) => field.field_id !== 'TFS.FIELD.IS_CUSTOM')
      };
    });
    setTableData(filteredData);
  }, [assetDetails?.metadata_collection?.metadata_element_list]);


  useEffect(() => {
    // Initialize accordionState with all IDs set to true (open by default)
    const initialAccordionState = assetDetails?.metadata_collection?.metadata_element_list?.reduce(
      (state: { [key: string]: boolean }, assetMetadata: any) => {
        state[assetMetadata.id] = true; // Set each accordion ID to true
        return state;
      },
      {}
    );
    setAccordionState(initialAccordionState || {});
  }, [assetDetails]);

  const shouldShowElement = (element: any): boolean => {
    if (element.is_tabular === 'Y') {
      // Find all elements in the same table (same meta_table_id)
      const tableElements = assetDetails?.metadata_collection?.metadata_element_list
        ?.flatMap((group: any) => group.metadata_element_list)
        ?.filter((el: any) => el.meta_table_id === element.meta_table_id);
      const hasValueId = tableElements?.some(
        (el: any) => Array.isArray(el.values) && el.values.some((v: any) => v.id)
      ) ?? false;
      return hasValueId; // Return true if at least one element in the table has a value.id
    }
    if (element.is_displayable === 'Y' && element?.value?.id) {
      return true; // Show elements that are displayable and have a value
    }
    return false; // Hide all other elements
  };

  const hasValue = (metadataElementList: any[]): boolean => {
    return metadataElementList.some((element) => shouldShowElement(element));
  };

  const handleAccordionToggle = (id: string) => {
    setAccordionState((prevState) => ({
      ...prevState,
      [id]: !prevState[id], // Toggle the open state for the specific accordion item
    }));
  };

  const renderTableRows = (element: any) => {
    const hasValues = element.elements.some(
      (field: any) => Array.isArray(field.values) && field.values.length > 0 && field.values.some((value: any) => value.display_value || value.id)
    );
    if (!hasValues) {
      return null; // Return null if all values arrays are empty
    }

    if (!element || !element.elements || element.elements.length === 0) {
      return null; // Return null if element is empty or has no elements
    }
    // Find the maximum number of rows needed
    const rowCount = Math.max(...element.elements.map((field: any) => field.values.length));

    return (
      <div className="asset-table-row">
        <table>
          <thead>
            <tr>
              {element.elements.map((field: any, index: number) => (
                <th key={index}>{field.prompt}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rowCount }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {element.elements.map((field: any, colIndex: any) => (
                  <td key={colIndex}>
                    {field.values[rowIndex] ? domParser(field.values[rowIndex].display_value) || domParser(field.values[rowIndex].id) : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const getIndex = (element: any): boolean => {
    const requiredElement = tableData?.find(
      (table: any) => table.meta_table_id === element.meta_table_id
    );
    let index = requiredElement?.elements?.findIndex(
      (el: any) => el.field_id === element.field_id
    );
    if (index > 1) {
      return false; // If index is greater than 1, return false
    }
    return index === 0; // Return true if index is 0, false otherwise
  };

  return (
    <div className="right-section" style={{ width: '100%' }}>
      <div className="right-section-content">
        <CoreAccordion>
          {assetDetails?.metadata_collection?.metadata_element_list?.map((assetMetadata: any) =>
            hasValue(assetMetadata.metadata_element_list) ? (
              <CoreAccordionItem
                open={accordionState[assetMetadata.id] || false} // Use the state to control open/close
                key={assetMetadata.id}
                animated
                label={assetMetadata.name}
                onAccordionToggle={() => handleAccordionToggle(assetMetadata.id)} // Pass the unique ID to toggle
              >
                <div slot ="label" style={{fontSize:'16px', fontWeight:'500'}}>{assetMetadata.name}</div>
                <div slot="content" className="single-upload-content-item">
                  {assetMetadata?.metadata_element_list?.map((element: any) =>
                    shouldShowElement(element) ? (
                      element.is_tabular !== 'Y' || (element.is_tabular === 'Y' && getIndex(element)) ? (
                        <div
                          key={element.field_id}
                          id={element.field_id}
                          className={`asset-${element.is_tabular === 'Y' ? 'table-row' : 'field-row'} asset-${assetMetadata.id
                            .toLowerCase()
                            .replaceAll('.', '-')}`}
                        >
                          {element.is_tabular === 'Y' && getIndex(element) ? (
                            renderTableRows(
                              tableData?.find(
                                (table: any) =>
                                  table?.meta_table_id === element?.meta_table_id
                              )
                            )
                          ) : (
                            <div className="field-item">
                              <label>{element.prompt}</label>
                              {element.datatype === 'DATE' ? 
                                <p>{new Date(element?.value?.id).toLocaleDateString()}</p> 
                                :
                                <p>{element?.value?.display_value || element?.value?.id}</p>}
                            </div>
                          )}
                        </div>
                      ) : null
                    ) : null
                  )}
                </div>
              </CoreAccordionItem>
            ) : null
          )}
        </CoreAccordion>
      </div>
    </div>
  );
};

export default RightSection;